import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { FilterUsersDialogComponent } from '../../dialogs/filter-users-dialog/filter-users-dialog.component';
import { DialogComponent } from '../../shared/ui/dialog/dialog.component';
import { SelectUsersWizardStepComponent } from '../dialogs/select-users-wizard-step/select-users-wizard-step.component';
import { IUserSessionData, UserType } from '../../store/models/user.model';
import { SelectRoleWizardStepComponent } from '../dialogs/select-role-wizard-step/select-role-wizard-step.component';
import { DataSourceParams, ITableColumn } from '../../store/models/table.model';
import { ApiDataSource } from '../../datasources/api.datasrouce';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import {
  EAccountReportStatus,
  IAccountReportCloserColumns,
  IAccountReportUsersColumns,
} from '../../store/models/account.model';
import { AccountService } from '../../store/services/account.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { EventService } from '../../store/services/event.service';
import { IWebSocketMessageGeneric } from '../../store/services/web-socket.service';
import { SessionService } from '../../store/services/session.service';
import { LeftMenuService } from 'app/store/services/left-menu.service';

export const REPORT_EVENT_NAME = 'report_event_notification';
export const REPORT_WS_TOPIC = 'report';
export interface IReportEvent {
  completed: boolean;
  file: string;
  message?: string;
  result?: string;
  pushTo?: number;
  keyId?: number;
}
export interface IReportWsTopic extends IWebSocketMessageGeneric{
  result: string;
  file: string;
  message: string;
  pushTo: number;
}

@Component({
  selector: 'app-list-reports',
  templateUrl: './list-reports.component.html',
  styleUrls: ['./list-reports.component.scss'],
})
export class ListReportsComponent implements OnInit, OnDestroy {

  private static readonly FRANCHISEE_CLOSER_COLUMN_IDS = [
    'discount_code',
    'franchisee_share',
    'ref_share',
    'last_charge_date',
    'last_charge_amount',
    'prior_charge_date',
    'prior_charge_amount',
    'trial',
  ];

  currentUsersSelection: number[] = [];
  currentRole: UserType;
  useAllUsers: boolean;
  includeDeletedSubordinates = false;
  filterButtonProperties: Button = {
    text: 'Filter users',
    size: ButtonSize.NORMAL,
    color: ButtonColor.ORANGE,
    onClick: () => this.showFilterWizard(),
  };
  downloadButtonProperties: Button = {
    text: 'Generate Contacts CSV',
    size: ButtonSize.NORMAL,
    color: ButtonColor.BLUE,
    onClick: () => this.downloadAllReports(EAccountReportStatus.CONTACTS_ONLY),
  };
  downloadCloserButtonProperties: Button = {
    text: 'Generate Closer CSV',
    size: ButtonSize.NORMAL,
    color: ButtonColor.BLUE,
    onClick: () => this.downloadAllReports(EAccountReportStatus.CONTACTS_AND_CLOSER),
  };
  reportDownloadInProgress: boolean;
  loadClosers: boolean;
  baseDataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'account_id',
    sortDir: 'desc',
    filters: {
      download: 'false',
    },
  };
  usersTableColumns: ITableColumn<IAccountReportUsersColumns>[] = [
    {
      id: 'user_contact_id',
      title: 'User Contact ID',
      sortable: true,
    },
    {
      id: 'user_id',
      title: 'User ID',
      sortable: true,
    },
    {
      id: 'first_name',
      title: 'First Name',
      sortable: true,
    },
    {
      id: 'last_name',
      title: 'Last Name',
      sortable: true,
    },
    {
      id: 'company',
      title: 'Company',
      sortable: true,
    },
    {
      id: 'email',
      title: 'Email',
      sortable: true,
    },
    {
      id: 'title',
      title: 'Title',
      sortable: true,
    },
    {
      id: 'address',
      title: 'Address',
      sortable: true,
    },
    {
      id: 'city',
      title: 'City',
      sortable: true,
    },
    {
      id: 'state',
      title: 'State',
      sortable: true,
    },
    {
      id: 'zip',
      title: 'Zip',
      sortable: true,
    },
    {
      id: 'phone',
      title: 'Phone',
      sortable: true,
    },
    {
      id: 'date_added_to_retreva',
      title: 'Date Added',
      isDateColumn: true,
      dateFormat: 'MM/DD/YYYY',
      sortable: true,
    },
    {
      id: 'current_status',
      title: 'Status',
      sortable: true,
    },
    {
      id: 'email_position',
      title: 'Email Position',
      sortable: true,
    },
    {
      id: 'next_email_at',
      title: 'Next Email At',
      isDateColumn: true,
      dateFormat: 'MM/DD/YYYY',
      sortable: true,
    },
    {
      id: 'vip',
      title: 'Vip',
      sortable: true,
      renderer: row => row.vip === 'true' ? 'Yes' : 'No',
    },
    {
      id: 'status',
      title: 'Status',
      sortable: true,
    },
    {
      id: 'bounced',
      title: 'Bounced',
      sortable: true,
      renderer: row => row.bounced === 'true' ? 'Yes' : 'No',
    },
    {
      id: 'current_pipeline_step',
      title: 'Pipeline Step',
      sortable: true,
    },
    {
      id: 'current_product',
      title: 'Product',
      sortable: true,
    },
    {
      id: 'Category',
      title: 'Category',
      sortable: true,
    },
    {
      id: 'won_amount',
      title: 'Won amount',
      sortable: true,
    },
    {
      id: 'source',
      title: 'Source',
      sortable: true,
    },
    {
      id: 'industry1',
      title: 'Industry',
      sortable: true,
    },
    {
      id: 'private_note',
      title: 'Private Note',
      sortable: true,
    },
    {
      id: 'tags',
      title: 'Tags',
      sortable: true,
    },
    {
      id: 'variable_1',
      title: 'Variable 1',
      sortable: true,
    },
    {
      id: 'variable_2',
      title: 'Variable 2',
      sortable: true,
    },
    {
      id: 'variable_3',
      title: 'Variable 3',
      sortable: true,
    },
  ];
  usersTableDataSource: ApiDataSource<any>;
  usersTableDatasourceParams: DataSourceParams;
  closerTableColumns: ITableColumn<IAccountReportCloserColumns>[] = [
    {
      id: 'first_name',
      title: 'First Name',
      sortable: true,
    },
    {
      id: 'last_name',
      title: 'Last Name',
      sortable: true,
    },
    {
      id: 'email',
      title: 'Email',
      sortable: true,
    },
    {
      id: 'company',
      title: 'Company',
      sortable: true,
    },
    {
      id: 'city',
      title: 'City',
      sortable: true,
    },
    {
      id: 'state',
      title: 'State',
      sortable: true,
    },
    {
      id: 'zip',
      title: 'Zip',
      sortable: true,
    },
    {
      id: 'country',
      title: 'Country',
      sortable: true,
    },
    {
      id: 'identifier_number',
      title: 'Identifier Number',
      sortable: true,
    },
    {
      id: 'partner_email',
      title: 'Partner Email',
      sortable: true,
    },
    {
      id: 'manager_email',
      title: 'Manager Email',
      sortable: true,
    },
    {
      id: 'retreva_service',
      title: 'Retreva Service',
      sortable: true,
    },
    {
      id: 'account_created_at',
      title: 'Account Created At',
      isDateColumn: true,
      dateFormat: 'MM/DD/YYYY',
      sortable: true,
    },
    {
      id: 'account_deleted_on',
      title: 'Account Deleted On',
      isDateColumn: true,
      dateFormat: 'MM/DD/YYYY hh:mm a',
      sortable: true,
      disabled: true,
    },
    {
      id: 'account_note',
      title: 'Account Note',
      sortable: true,
    },
    {
      id: 'invoice_note',
      title: 'Integration Note',
      sortable: true,
    },
    {
      id: 'class_of_service',
      title: 'Class of Service',
      sortable: true,
    },
    {
      id: 'service_flag',
      title: 'Service Flag',
      sortable: true,
    },
    {
      id: 'optional_note_a',
      title: 'Option Note A',
      sortable: true,
    },
    // Franchisee mode only (enabled once the API reports extra_data.franchisee_mode = true)
    {
      id: 'discount_code',
      title: 'Discount code',
      sortable: true,
      disabled: true,
    },
    {
      id: 'franchisee_share',
      title: 'Franchisee Share',
      sortable: true,
      disabled: true,
    },
    {
      id: 'ref_share',
      title: 'Ref Share',
      sortable: true,
      disabled: true,
    },
    {
      id: 'optional_note_b',
      title: 'Option B',
      sortable: true,
    },
    // Franchisee mode only
    {
      id: 'last_charge_date',
      title: 'Last Charge Date',
      isDateColumn: true,
      dateFormat: 'MM/DD/YYYY',
      sortable: true,
      disabled: true,
    },
    {
      id: 'last_charge_amount',
      title: 'Last Charge Amount',
      sortable: true,
      disabled: true,
    },
    {
      id: 'prior_charge_date',
      title: 'Prior Charge Date',
      isDateColumn: true,
      dateFormat: 'MM/DD/YYYY',
      sortable: true,
      disabled: true,
    },
    {
      id: 'prior_charge_amount',
      title: 'Prior Charge Amount',
      sortable: true,
      disabled: true,
    },
    {
      id: 'account_id',
      title: 'Account ID',
      sortable: true,
    },
    {
      id: 'other',
      title: 'Other',
      sortable: true,
    },
    {
      id: 'spam',
      title: 'Spam',
      sortable: true,
    },
    {
      id: 'remove',
      title: 'Remove',
      sortable: true,
    },
    {
      id: 'inactive',
      title: 'Inactive',
      sortable: true,
    },
    {
      id: 'cycle',
      title: 'Cycle',
      sortable: true,
    },
    {
      id: 'follow',
      title: 'Follow',
      sortable: true,
    },
    {
      id: 'opportunities',
      title: 'Opportunities',
      sortable: true,
    },
    {
      id: 'current_meetings',
      title: 'Meetings',
      sortable: true,
    },
    {
      id: 'current_orders',
      title: 'Orders',
      sortable: true,
    },
    {
      id: 'current_quotes',
      title: 'Quotes',
      sortable: true,
    },
    {
      id: 'lost',
      title: 'Total Lost',
      sortable: true,
    },
    {
      id: 'won',
      title: 'Won',
      sortable: true,
    },
    {
      id: 'amount_gained',
      title: 'Amount gained',
      sortable: true,
    },
    {
      id: 'vsm_messages',
      title: 'Vsm Messages',
      sortable: true,
    },
    {
      id: 'vsm_released',
      title: 'Vsm Released',
      sortable: true,
    },
    {
      id: 'visible_messages',
      title: 'Visible Messages',
      sortable: true,
    },
    {
      id: 'unread_visible_messages',
      title: 'Unread Visible Messages',
      sortable: true,
    },
    {
      id: 'red_no_matches',
      title: 'Red No Matches',
      sortable: true,
    },
    {
      id: 'contacts_in_service',
      title: 'Contacts Service',
      sortable: true,
    },
    {
      id: 'active_contacts',
      title: 'Active Contacts',
      sortable: true,
    },
    {
      id: 'contacts_in_campaign',
      title: 'Contacts In Campaign',
      sortable: true,
    },
    {
      id: 'contacts_added_last_30_days',
      title: 'Contacts Added (last month)',
      sortable: true,
    },
    {
      id: 'contacts_added_last_90_days',
      title: 'Contacts Added (last 3 months)',
      sortable: true,
    },
    {
      id: 'contacts_added_in_last_year',
      title: 'Contacts Added (last year)',
      sortable: true,
    },
    {
      id: 'integration_data_sync_on',
      title: 'Integration Data Sync',
      sortable: true,
    },
    {
      id: 'integration_total_contacts_added',
      title: 'Integration Total Contacts',
      sortable: true,
    },
    {
      id: 'integration_contacts_added_last_30_days',
      title: 'Integration Contacts Added (last month)',
      sortable: true,
    },
    // Franchisee mode only
    {
      id: 'trial',
      title: 'Trial',
      sortable: true,
      disabled: true,
      renderer: row => row.trial ? 'Yes' : 'No',
    },
  ];
  closerTableDataSource: ApiDataSource<any>;
  closerTableDataSourceParams: DataSourceParams;
  showReportWarning: boolean;
  /** Whether the closer report currently carries the franchisee revenue columns (decided by the API). */
  franchiseeMode = false;

  private sessionData: IUserSessionData;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private readonly store$: Store<IReducers>,
    private readonly http: HttpClient,
    private readonly accountsService: AccountService,
    private readonly eventService: EventService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly sessionService: SessionService,
    private readonly leftMenuService: LeftMenuService
  ) {
  }

  get filterApplied(): boolean {
    return this.useAllUsers || this.currentUsersSelection.length > 0;
  }

  get showIncludeDeleted(): boolean {
    return this.currentRole === UserType.FRANCHISE && !this.useAllUsers && this.currentUsersSelection.length > 0;
  }

  configureDataSource() {
    this.usersTableDataSource = new ApiDataSource(this.http, environment.apiUrl + '/v1/users/report');
    this.usersTableDataSource.onError
      .pipe(takeUntil(this.destroy$))
      .subscribe((err: HttpErrorResponse) => this.store$.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: err.error,
            type: 'error'
          }
        }
      })));
    this.closerTableDataSource = new ApiDataSource(this.http, environment.apiUrl + '/v1/users/report');
    this.closerTableDataSource.onError
      .pipe(takeUntil(this.destroy$))
      .subscribe((err: HttpErrorResponse) => this.store$.dispatch(openSnackbar({
        snackbarData: {
          data: {
            message: err.error,
            type: 'error'
          }
        }
      })));
    this.closerTableDataSource.onExtraData()
      .pipe(takeUntil(this.destroy$))
      .subscribe(extraData => this.setFranchiseeMode(!!extraData?.franchisee_mode));
  }

  /**
   * Toggles the franchisee-only closer columns. The column objects are mutated in place so the table
   * re-renders through its `disabled` filter without triggering a data reload.
   */
  private setFranchiseeMode(enabled: boolean): void {
    if (this.franchiseeMode === enabled) {
      return;
    }
    this.franchiseeMode = enabled;
    this.closerTableColumns
      .filter(column => ListReportsComponent.FRANCHISEE_CLOSER_COLUMN_IDS.includes(column.id))
      .forEach(column => column.disabled = !enabled);
    this.changeDetectorRef.detectChanges();
  }

  ngOnInit(): void {
    this.leftMenuService.expand('reports');
    this.sessionService.onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.sessionData = sessionData;
        const reportInProgressItem = localStorage.getItem(REPORT_EVENT_NAME);

        if (reportInProgressItem) {
          const reportData = JSON.parse(reportInProgressItem);
          if (reportData.pushTo === this.sessionData.id) {
            this.reportDownloadInProgress = true;
          }
        }
        this.eventService.on(REPORT_EVENT_NAME)
          .pipe(takeUntil(this.destroy$))
          .subscribe(payload => {
            if (typeof payload !== 'boolean') {
              return;
            }

            this.reportDownloadInProgress = payload;
            if (this.reportDownloadInProgress === false) {
              this.currentRole = null;
              this.currentUsersSelection = [];
              this.includeDeletedSubordinates = false;
              this.filterButtonProperties.text = `Filter users`;
              this.configureDataSource();
              this.configureTable();
            }
            this.changeDetectorRef.detectChanges();
          });
        this.configureDataSource();
      });
  }

  showFilterWizard() {
    this.store$.dispatch(openDialog({
      dialogData: {
        title: 'Filter Selection Wizard',
        content: FilterUsersDialogComponent,
        componentData: {
          currentUsersSelection: this.currentUsersSelection,
          currentRole: this.currentRole,
          includeDeletedSubordinates: this.includeDeletedSubordinates,
        },
        dialogMinWidth: '1100px',
        canClose: true,
        onClose: (dialog: DialogComponent) => {
          if (!dialog.result){
            return;
          }
          if (dialog.result.allUsers) {
            this.useAllUsers = true;
            this.includeDeletedSubordinates = false;
            this.configureTable();
            return;
          }
          this.useAllUsers = false;
          this.currentUsersSelection = dialog.result[SelectUsersWizardStepComponent.NAME].users;
          this.filterButtonProperties.text = `Filter users (${this.currentUsersSelection.length})`;
          this.currentRole = dialog.result[SelectRoleWizardStepComponent.NAME].role;
          this.includeDeletedSubordinates = this.currentRole === UserType.FRANCHISE
            && !!dialog.result[SelectUsersWizardStepComponent.NAME].includeDeleted;
          this.configureTable();
        },
      },
    }));
  }

  configureTable(): void {
    this.usersTableDatasourceParams = {
      ...this.baseDataSourceParams,
      filters: {
        ...this.baseDataSourceParams.filters,
        type: 'contacts',
        account_id: this.useAllUsers ? '' : this.currentUsersSelection.join(','),
      },
    };
    const closerFilters: { [key: string]: any } = {
      ...this.baseDataSourceParams.filters,
      type: 'users',
      account_id: this.useAllUsers ? '' : this.currentUsersSelection.join(','),
    };
    if (this.showIncludeDeleted && this.includeDeletedSubordinates) {
      closerFilters.with_deleted = 'true';
    }
    this.closerTableColumns
      .filter(column => column.id === 'account_deleted_on')
      .forEach(column => column.disabled = !(this.showIncludeDeleted && this.includeDeletedSubordinates));
    this.closerTableDataSourceParams = {
      ...this.baseDataSourceParams,
      filters: closerFilters,
    };
  }

  downloadAllReports(reportType: EAccountReportStatus) {
    this.reportDownloadInProgress = true;
    this.showReportWarning = true;
    this.store$.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: 'Your report is being downloading...',
          type: 'success',
        },
      },
    }));
    const withDeleted = reportType === EAccountReportStatus.CONTACTS_AND_CLOSER
      && this.showIncludeDeleted
      && this.includeDeletedSubordinates;
    this.accountsService.downloadContactsReport(reportType, this.currentUsersSelection, true, 'csv', withDeleted)
      .subscribe((response: HttpResponse<Blob>) => {
          const fileName = response.headers.get('Content-Disposition')
            .split(';')
            .map(f => f.trim())
            .filter(f => f.indexOf('filename=') !== -1)[0]
            .split('=')[1]
            .replace(new RegExp('"', 'g'), '');
          const url = window.URL.createObjectURL(response.body);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName.trim();
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          this.reportDownloadInProgress = false;
          this.showReportWarning = false;
        }
        , error => {
          this.reportDownloadInProgress = false;
          this.showReportWarning = false;
          if (error instanceof HttpErrorResponse && error.error.code > 1005) {
            this.store$.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: 'Your report is being created. Please, wait for this report link to be downloaded',
                  type: 'warning',
                },
                duration: 5000
              }
            }));
            const reportData: any = {
              completed: false,
              file: null,
              pushTo: this.sessionData.id,
              keyId: error.error.code - 1005,
            };
            localStorage.setItem(REPORT_EVENT_NAME, JSON.stringify(reportData));
            this.eventService.broadcast<IReportEvent>(REPORT_EVENT_NAME, reportData);
            this.reportDownloadInProgress = true;
            return;
          }

          this.store$.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: error.error,
                  type: 'error',
                },
              },
            }));
        });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  onTabChange($event: MatTabChangeEvent) {
    if (!this.loadClosers && $event.index === 1) {
      this.loadClosers = true;
    }
  }

}
